import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { addHours, isAfter, isBefore, differenceInSeconds } from 'date-fns'
import { useUserStore } from '@/stores/user'
import { useTimeStore } from '@/stores/time'
import { useI18n } from 'vue-i18n'
import { ACTIVE_SHIFTS } from '@/util/constants'
import type { ShiftTypeDTO, UserRightsEnum } from '@/generated'

export function useShift() {
  const { t } = useI18n()
  const userStore = useUserStore()
  const { user, isIncognito, endOfShiftDate, hasSelectedShiftDuration } = storeToRefs(userStore)
  const { currentTime } = storeToRefs(useTimeStore())

  const isFirstOrBackupShift = (value: ShiftTypeDTO | UserRightsEnum | undefined): boolean => {
    return ACTIVE_SHIFTS.includes(value as ShiftTypeDTO)
  }

  const isShiftActive = computed(() => isFirstOrBackupShift(user.value?.currentShift))

  const isEndingSoon = computed(() => {
    if (!endOfShiftDate.value) return false
    const oneHourFromNow = addHours(currentTime.value, 1)
    return (
      isBefore(endOfShiftDate.value, oneHourFromNow) &&
      isAfter(endOfShiftDate.value, currentTime.value)
    )
  })

  const isShiftEnded = computed(() => {
    if (!endOfShiftDate.value) return false
    return isAfter(currentTime.value, endOfShiftDate.value)
  })

  const timeToEnd = computed(() => {
    if (!endOfShiftDate.value) return '–'
    const diffInSeconds = differenceInSeconds(endOfShiftDate.value, currentTime.value)
    const minutes = Math.floor(diffInSeconds / 60)
    const seconds = diffInSeconds % 60
    return `${minutes} ${t('Min.')} ${seconds.toString().padStart(2, '0')} ${t('Sec.')}`
  })

  const shouldShowShiftBanner = computed(
    () => isIncognito.value || (isShiftActive.value && hasSelectedShiftDuration.value)
  )

  return {
    isIncognito,
    isShiftActive,
    isEndingSoon,
    isShiftEnded,
    timeToEnd,
    shouldShowShiftBanner,
    endOfShiftDate,
    isFirstOrBackupShift
  }
}
